/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  Pagination — shared footer control for every table and card grid
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  WHAT WAS WRONG WITH THE OLD ONE
 *  -------------------------------
 *  • The prev/next controls were the typographic characters "‹" and "›", not
 *    icons — so their weight, size and optical alignment never matched the
 *    lucide icons used everywhere else on the page.
 *  • They were 24×24px. The accessibility floor for a touch target is 44px,
 *    and even with a mouse a 24px square is a poor click target.
 *  • `disabled:opacity-20` made a disabled control almost invisible, so "you
 *    are on the last page" and "this button is broken" looked identical.
 *  • There were no page numbers at all. On 15 items across 2 pages you could
 *    not see how many pages existed, let alone jump to one.
 *
 *  TWO MODES, because the data layer has two shapes
 *  ------------------------------------------------
 *  numbered   — `totalPages` is known (client-side filtering). Renders real
 *               page numbers with an ellipsis window, so any page is one click.
 *  sequential — cursor/token pagination where only "is there a next page" is
 *               knowable (PatientsTable). Renders prev/next plus the current
 *               page number, and never claims a total it cannot know.
 *
 *  The mode is inferred: pass `totalPages` for numbered, `hasNextPage` for
 *  sequential. Nothing else changes at the call site.
 */

import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Dropdown } from './Dropdown';

export interface PaginationProps {
  isDark: boolean;
  /** Zero-based current page. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;

  /** Numbered mode. Omit when the total is unknowable. */
  totalPages?: number;
  /** Sequential mode. Used only when `totalPages` is not given. */
  hasNextPage?: boolean;

  /** Total row count, for the "1–10 of 15" summary. */
  totalItems?: number;
  /** Noun for the summary line — "specialists", "patients", "reports". */
  itemLabel?: string;

  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;

  className?: string;
}

/**
 * Page numbers with an ellipsis window: always the first and last page, the
 * current page and one neighbour either side. Keeps the control a fixed width
 * whether there are 3 pages or 300.
 */
function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);

  const out: (number | 'gap')[] = [0];
  const from = Math.max(1, current - 1);
  const to = Math.min(total - 2, current + 1);

  if (from > 1) out.push('gap');
  for (let i = from; i <= to; i++) out.push(i);
  if (to < total - 2) out.push('gap');

  out.push(total - 1);
  return out;
}

const NavBtn: React.FC<{
  isDark: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}> = ({ isDark, disabled, onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={[
      'inline-flex h-8 w-8 items-center justify-center rounded-lg outline-none',
      'transition-colors duration-150',
      'focus-visible:ring-2 focus-visible:ring-brand-500/45',
      disabled
        ? isDark ? 'cursor-not-allowed text-slate-600' : 'cursor-not-allowed text-slate-300'
        : isDark
          ? 'text-slate-300 hover:bg-white/[0.08] hover:text-white'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
    ].join(' ')}
  >
    {children}
  </button>
);

export const Pagination: React.FC<PaginationProps> = ({
  isDark, page, pageSize, onPageChange,
  totalPages, hasNextPage, totalItems, itemLabel,
  pageSizeOptions, onPageSizeChange, className = '',
}) => {
  const numbered = typeof totalPages === 'number';
  const lastPage = numbered ? Math.max((totalPages as number) - 1, 0) : Infinity;

  const canPrev = page > 0;
  const canNext = numbered ? page < lastPage : !!hasNextPage;

  const pages = useMemo(
    () => (numbered ? pageWindow(page, Math.max(totalPages as number, 1)) : []),
    [numbered, page, totalPages],
  );

  const from = totalItems === 0 ? 0 : page * pageSize + 1;
  const to = typeof totalItems === 'number'
    ? Math.min((page + 1) * pageSize, totalItems)
    : (page + 1) * pageSize;

  return (
    <nav
      aria-label="Pagination"
      className={[
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-3',
        className,
      ].join(' ')}
    >
      {/* Summary — the numbers carry the emphasis, not the words */}
      <p className={['text-[12px] tabular-nums', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
        {typeof totalItems === 'number' ? (
          totalItems === 0 ? (
            <span>No {itemLabel ?? 'results'}</span>
          ) : (
            <>
              <span className={isDark ? 'font-semibold text-slate-200' : 'font-semibold text-slate-700'}>
                {from}–{to}
              </span>
              <span className="mx-1 opacity-60">of</span>
              <span className={isDark ? 'font-semibold text-slate-200' : 'font-semibold text-slate-700'}>
                {totalItems}
              </span>
              {itemLabel && <span className="ml-1">{itemLabel}</span>}
            </>
          )
        ) : (
          <span>Page {page + 1}</span>
        )}
      </p>

      <div className="flex items-center gap-2">
        {pageSizeOptions && onPageSizeChange && (
          <Dropdown<number>
            size="sm"
            align="right"
            menuMinWidth={132}
            isDark={isDark}
            ariaLabel="Rows per page"
            value={pageSize}
            onChange={onPageSizeChange}
            options={pageSizeOptions.map((n) => ({ value: n, label: `${n} per page` }))}
          />
        )}

        <div className={['h-5 w-px', isDark ? 'bg-white/[0.10]' : 'bg-slate-200'].join(' ')} />

        <div className="flex items-center gap-0.5">
          {numbered && (totalPages as number) > 7 && (
            <NavBtn isDark={isDark} disabled={!canPrev} onClick={() => onPageChange(0)} label="First page">
              <ChevronsLeft size={15} />
            </NavBtn>
          )}

          <NavBtn isDark={isDark} disabled={!canPrev} onClick={() => onPageChange(page - 1)} label="Previous page">
            <ChevronLeft size={16} />
          </NavBtn>

          {numbered ? (
            <div className="flex items-center gap-0.5">
              {pages.map((p, i) =>
                p === 'gap' ? (
                  <span
                    key={`gap-${i}`}
                    aria-hidden="true"
                    className={['w-5 text-center text-[12px]', isDark ? 'text-slate-600' : 'text-slate-300'].join(' ')}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onPageChange(p)}
                    aria-label={`Page ${p + 1}`}
                    aria-current={p === page ? 'page' : undefined}
                    className={[
                      'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg px-2',
                      'text-[12.5px] font-semibold tabular-nums outline-none',
                      'transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand-500/45',
                      p === page
                        ? isDark ? 'bg-brand-500/25 text-white' : 'bg-brand-600 text-white'
                        : isDark
                          ? 'text-slate-400 hover:bg-white/[0.08] hover:text-white'
                          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
                    ].join(' ')}
                  >
                    {p + 1}
                  </button>
                ),
              )}
            </div>
          ) : (
            /* Cursor mode — show where you are, never invent a total */
            <span className={[
              'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg px-2.5',
              'text-[12.5px] font-semibold tabular-nums',
              isDark ? 'bg-brand-500/25 text-white' : 'bg-brand-600 text-white',
            ].join(' ')}>
              {page + 1}
            </span>
          )}

          <NavBtn isDark={isDark} disabled={!canNext} onClick={() => onPageChange(page + 1)} label="Next page">
            <ChevronRight size={16} />
          </NavBtn>

          {numbered && (totalPages as number) > 7 && (
            <NavBtn isDark={isDark} disabled={!canNext} onClick={() => onPageChange(lastPage)} label="Last page">
              <ChevronsRight size={15} />
            </NavBtn>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Pagination;
